// Feature: M004-F0696 (M001-D033 §8). Test: T-M004-0170.
//
// App-declared permission keys, rendered BELOW the core keys of the existing
// Organisation Permissions matrix (M004-F0261). Everything shown comes from
// the server's `applicationPermissions` block; nothing here is keyed on an
// application, a key name or a user role.
import React from 'react';
import {
  cellSource,
  displayedCell,
  loginKeyWarnings,
  pendingKey,
} from './appPermissionCatalogue';

const optionFor = (override) => (override === null ? 'inherit' : (override ? 'allow' : 'deny'));
const valueFor = (option) => (option === 'inherit' ? null : option === 'allow');

export default function AppPermissionMatrix({
  applications,
  pendingChanges,
  editable,
  canDeclare,
  onChange,
  onOpenDeclaration,
}) {
  if (!applications.length) return null;
  const warnings = loginKeyWarnings(applications, pendingChanges);

  return (
    <section className="app-permission-matrix" aria-labelledby="app-permission-matrix-heading" data-testid="app-permission-matrix">
      <h4 id="app-permission-matrix-heading">Application permissions</h4>
      <p className="app-permission-help">
        Declared by each application and enforced by it. A cell shows what the role gets in this
        organisation; unset cells inherit the registration default.
        {editable ? '' : ' Read only for this account.'}
      </p>
      {warnings.length > 0 && (
        <ul className="role-management-notice app-permission-warnings" role="alert" data-testid="app-permission-warnings">
          {warnings.map((warning) => (
            <li key={warning.id} data-testid="app-permission-warning">{warning.message}</li>
          ))}
        </ul>
      )}
      {applications.map((app) => (
        <div key={app.appCode} className="app-permission-app" data-testid={`app-permission-section-${app.appCode}`}>
          <div className="app-permission-app-heading">
            <h5>{app.appName}</h5>
            {canDeclare && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => onOpenDeclaration(app)}
                data-testid={`app-permission-declaration-open-${app.appCode}`}
              >
                Edit declaration
              </button>
            )}
          </div>
          <div className="permission-matrix-wrap">
            <table className="permission-matrix app-permission-table">
              <thead>
                <tr>
                  <th scope="col">Permission</th>
                  {app.roles.map((role) => <th key={role} scope="col">{role}</th>)}
                </tr>
              </thead>
              <tbody>
                {app.keys.map((key) => (
                  <tr key={key.name} data-testid={`app-permission-row-${key.name}`}>
                    <th scope="row">
                      <span className="app-permission-label">{key.label}</span>
                      {key.loginRequired && (
                        <span className="status-badge info" data-testid={`app-permission-login-${key.name}`}>login</span>
                      )}
                      {key.retired && (
                        <span className="status-badge" data-testid={`app-permission-retired-${key.name}`}>retired</span>
                      )}
                      {key.description && <span className="app-permission-description">{key.description}</span>}
                    </th>
                    {app.roles.map((role) => {
                      const pending = pendingChanges[pendingKey(key.name, role)];
                      const shown = displayedCell(key.cells[role], pending);
                      const cellEditable = editable && !key.retired && Boolean(key.cells[role]);
                      return (
                        <td key={role} data-testid={`app-permission-cell-${key.name}-${role}`}>
                          <span className="app-permission-effective">{shown.effective ? 'Allowed' : 'Denied'}</span>
                          <span className="app-permission-source" data-testid={`app-permission-source-${key.name}-${role}`}>
                            {cellSource(shown)}
                          </span>
                          <select
                            aria-label={`${key.label} for ${role}`}
                            value={optionFor(shown.override)}
                            disabled={!cellEditable}
                            onChange={(event) => onChange(key, role, valueFor(event.target.value))}
                            data-testid={`app-permission-select-${key.name}-${role}`}
                          >
                            <option value="inherit">{`Inherit (${shown.default ? 'allowed' : 'denied'})`}</option>
                            <option value="allow">Allowed</option>
                            <option value="deny">Denied</option>
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </section>
  );
}
